import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import { Button, EmptyState, LoadingState } from '@hostesa/ui';
import { Link, Navigate } from '@tanstack/react-router';
import { firstAccessibleHotelPath } from '../lib/slug-resolution';

/**
 * Landing for authenticated users with no resolvable hotel. Operators
 * (hostesa_admin) belong in the back-office, so send them there; anyone with a
 * hotel membership is redirected to their hotel; a hotel-less account gets an
 * explanation instead of a dead end.
 */
export function OnboardingPage() {
  const { t } = useT('platform');
  const { memberships, loading } = useAuth();

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]">
        <LoadingState data-testid="onboarding-landing-loading" title={t('onboarding.title')} />
      </main>
    );
  }

  const hotelPath = firstAccessibleHotelPath(memberships);
  if (hotelPath) return <Navigate to={hotelPath} replace />;

  const isOperator = memberships.some((m) => m.role === 'hostesa_admin');
  if (isOperator) {
    const envUrl = import.meta.env.VITE_BACKOFFICE_BASE_URL;
    const backofficeUrl = typeof envUrl === 'string' ? envUrl : 'https://admin.hostesa.ai';
    return (
      <main
        data-testid="onboarding-page"
        className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]"
      >
        <EmptyState
          title={t('onboarding.operatorTitle')}
          description={t('onboarding.operatorDescription')}
          action={
            <Button asChild data-testid="onboarding-backoffice-btn">
              <a href={backofficeUrl}>{t('onboarding.openBackoffice')}</a>
            </Button>
          }
        />
      </main>
    );
  }

  return (
    <main
      data-testid="onboarding-page"
      className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]"
    >
      <EmptyState title={t('onboarding.title')} description={t('onboarding.description')} />
    </main>
  );
}

/**
 * Tenant-scoped no-access / not-found state. Used both for unknown slugs and
 * for routes the user lacks permission to reach. It deliberately reveals
 * nothing about whether a tenant/hotel exists elsewhere (master prompt §13).
 */
export function NoAccessPage() {
  const { t } = useT('platform');
  return (
    <main
      data-testid="no-access-page"
      className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]"
    >
      <EmptyState
        title={t('noAccess.title')}
        description={t('noAccess.description')}
        action={
          <Button asChild variant="secondary" data-testid="no-access-back-btn">
            <Link to="/login">{t('noAccess.backToReception')}</Link>
          </Button>
        }
      />
    </main>
  );
}

/** Generic catch-all not-found. */
export function NotFoundPage() {
  const { t } = useT('platform');
  return (
    <main
      data-testid="not-found-page"
      className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]"
    >
      <EmptyState
        title={t('notFound.title')}
        description={t('notFound.description')}
        action={
          <Button asChild variant="secondary" data-testid="not-found-back-btn">
            <Link to="/login">{t('notFound.backToReception')}</Link>
          </Button>
        }
      />
    </main>
  );
}

/** Shown when the authenticated user has no accessible hotel. */
export function NoHotelPage() {
  const { t } = useT('platform');
  return (
    <main
      data-testid="no-hotel-page"
      className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]"
    >
      <EmptyState title={t('noHotel.title')} description={t('noHotel.description')} />
    </main>
  );
}
